import NotifyMeDialog from '@/components/ProductFullDetail/notify-me-dialog';
import { Form } from '@/components/ui/form';
import { useAppContext } from '@/context/app';
import { useUserContext } from '@/context/user';
import { useAddToCart } from '@/hooks/ProductFullDetail/hooks/use-add-to-cart';
import { useFinalPriceAndRegularPrice } from '@/hooks/ProductFullDetail/hooks/use-final-price-and-regular-price';
import { useGetIsOutOfStock } from '@/hooks/ProductFullDetail/hooks/use-get-is-out-of-stock';
import { useGetIsPreOrderInfo } from '@/hooks/ProductFullDetail/hooks/use-get-is-pre-order-info';
import { useGetOptionCode } from '@/hooks/ProductFullDetail/hooks/use-get-option-code';
import { useGetProductPrice } from '@/hooks/ProductFullDetail/hooks/use-get-product-price';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { useGetMediaGalleryEntries } from '@/hooks/useGetMediaGalleryEntries';
import { capitalizeFirstLetter, formatString } from '@/lib/format-string';
import { findMatchingVariant } from '@/util/findMatchingProductVariant';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { Fragment, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useInView } from 'react-intersection-observer';
import { z } from 'zod';
import Price from '../Price';
import Rating from '../Rating';
import { AddCartButton } from './add-cart-button';
import { ProductBreadcrumb } from './product-breadcrumb';
import { ProductDescription } from './product-description';
import { ProductFormError } from './product-form-error';
import ProductImageCarousel from './product-image-carousel';
import { EarnPoints } from './earn-points';
import { SezzlePay } from './sezzle-pay';
import { ProductBenefitsAccordion } from './product-benefits-accordion';
import ShowAttributes from './show-attributes';
import { FindYourSize } from './find-your-size';
import { StandardOptions } from './standard-options';
import type { ConfigurableOption, ProductRoot, SelectedOptions, SelectedOptionsWithoutQuantity } from './types/product';
import { useOptionsSpecs } from './use-options-sepcs';

const PRODUCT_TITLE_CLASS = 'font-roboto-medium text-[24px] capitalize leading-7 lg_text-[26px] lg_leading-[30px]';

const ProductFullDetail = ({
  product,
  onAddReviewClick,
  onViewReviews,
  handleColorLabelChange,
  defaultValues,
  seoColorLabel,
  breadcrumbsData,
}: {
  product: ProductRoot;
  onAddReviewClick: () => void;
  onViewReviews: () => void;
  handleColorLabelChange: any;
  defaultValues: any;
  seoColorLabel: string;
  breadcrumbsData: any;
}) => {
  const [{ isSignedIn }] = useUserContext();
  const [notifyModalVisible, setNotifyModalVisible] = useState(false);
  const [{ isSticky }] = useAppContext();
  const { isMobile } = useIsMobile();
  const productOptionsRef = useRef<HTMLDivElement>(null);

  const createFormSchema = (options: ConfigurableOption[]) => {
    const schemaFields: Record<string, z.ZodTypeAny> = {};

    options?.forEach(item => {
      schemaFields[item?.attribute_code] = z.object(
        {
          uid: z.string().min(1),
          label: z.string(),
          option_attribute_id: z.string(),
          option_selection_value: z.number().optional(),
        },
        {
          message: `This is a required field!`,
        },
      );
    });

    schemaFields['quantity'] = z.string().min(1, {
      message: `This is a required field!`,
    });

    return z.object(schemaFields);
  };

  const FormSchema = createFormSchema(product.configurable_options);

  const form = useForm({
    resolver: zodResolver(FormSchema as any),
    defaultValues: defaultValues,
  });

  const allCPData = form.watch() as SelectedOptions;

  const allCpDataWithoutQuantity = Object.fromEntries(
    Object.entries(allCPData).filter(([key]) => key !== 'quantity'),
  ) as SelectedOptionsWithoutQuantity;

  const addToCartQty = form.watch('quantity');
  const sizeChartInfo = product.product_size_chart as any;
  const hasSizeChart = Boolean(sizeChartInfo?.content);
  const hasConfigurableOptions = Array.isArray(product.configurable_options) && product.configurable_options.length > 0;

  const { optionCodes } = useGetOptionCode({
    product,
  });

  const { optionSpecs, handleClick } = useOptionsSpecs({
    product,
  });

  const optionSelections = useMemo(() => {
    const optionMap = new Map();
    const options = product?.configurable_options || [];

    options.forEach(item => {
      const { attribute_id } = item;

      optionMap.set(attribute_id, undefined);
    });

    // Iterate through each property in the JSON object
    for (const [key, value] of Object.entries(allCpDataWithoutQuantity)) {
      if (typeof value === 'object' && value !== null) {
        const item = allCpDataWithoutQuantity[key];

        optionMap.set(item.option_attribute_id, item.option_selection_value);
      }
    }

    return optionMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCpDataWithoutQuantity]);

  const selectedOptionsArray = useMemo(() => {
    return Object.values(allCpDataWithoutQuantity)
      .filter(item => item && item.uid)
      .map(item => item.uid);
  }, [allCpDataWithoutQuantity]);

  const { isPreOrderInfo } = useGetIsPreOrderInfo({
    product,
    optionCodes,
    optionSelections,
  });
  const { productPrice } = useGetProductPrice({
    product,
    optionCodes,
    optionSelections,
  });
  const { finalPriceAndRegularPrice } = useFinalPriceAndRegularPrice({
    product,
    optionCodes,
    optionSelections,
  });
  const { mediaGalleryEntries } = useGetMediaGalleryEntries({
    product,
    optionCodes,
    optionSelections,
  });
  const { isOutOfStock } = useGetIsOutOfStock({
    product,
    optionCodes,
    optionSelections,
  });
  const { errorMessage, handleAddToCart, isAdding, addToCartStatus, failedToCart } = useAddToCart({
    product,
    mediaGalleryEntries,
    selectedOptionsArray,
  });
  const item = findMatchingVariant({
    optionCodes,
    optionSelections,
    variants: product.variants,
  });

  const { isPreOrder, note, label } = isPreOrderInfo || {};
  const productDetails = {
    description: product.description,
    shortDescription: product.short_description,
    name: product.name,
    price: productPrice?.final_price,
    sku: product.sku,
    finalPriceAndRegularPrice: finalPriceAndRegularPrice,
  };

  const finalPrice = productDetails?.finalPriceAndRegularPrice?.finalPrice;

  const regularPrice = productDetails?.finalPriceAndRegularPrice?.regularPrice;

  const hasSpecialPrice = finalPrice && regularPrice && finalPrice < regularPrice;
  console.log('hasSpecialPrice', finalPrice, regularPrice, hasSpecialPrice);
  const onSubmit = (values: any) => {
    if (isOutOfStock) {
      setNotifyModalVisible(true);

      return;
    }

    handleAddToCart({
      quantity: values.quantity as number,
    });
  };

  const transformedProductName = formatString(productDetails.name);
  const h1Title = seoColorLabel
    ? capitalizeFirstLetter(seoColorLabel) + ' ' + transformedProductName
    : transformedProductName;

  const productInfoHeader = (asH1: boolean) => (
    <>
      {product.serial_name ? (
        <div className=' mb-[5px] py-[5px] font-teko-medium text-[22px] font-medium leading-none lg_text-[26px]'>
          {' '}
          {product.serial_name}
        </div>
      ) : null}
      {asH1 ? <h1 className={PRODUCT_TITLE_CLASS}>{h1Title}</h1> : <div className={PRODUCT_TITLE_CLASS}>{h1Title}</div>}
      <p className='mt-2.5 font-roboto-light text-[16px] font-light leading-5 lg_mt-5 lg_text-base lg_leading-[26px]'>
        SKU: {productDetails.sku}
      </p>
      {product.review_count > 0 ? (
        <div className='mt-2.5 flex items-center gap-x-2.5 lg_mt-5'>
          <Rating
            rating={product.rating_summary}
            disabled
          />
          <button
            type='button'
            className='font-roboto-light text-[16px] font-light leading-5 underline lg_text-base lg_leading-[26px]'
            onClick={onViewReviews}
          >
            {product.review_count} Reviews
          </button>
        </div>
      ) : (
        <span
          className='font-roboto-light text-[16px] font-light leading-5 lg_text-base lg_leading-[26px]'
          onClick={onAddReviewClick}
        >
          Be the first to review this product
        </span>
      )}
    </>
  );

  const {
    ref: addCartBtnRef,
    inView: staticAddCartBtnInView,
    entry: addToCartBtnEntry,
  } = useInView({
    rootMargin: isSticky ? `${isMobile ? '-50px' : '-76px'} 0px 0px 0px` : '0px 0px 0px 0px',
    threshold: 0.5,
  });
  const staticAddCartBtnRectTop = addToCartBtnEntry?.boundingClientRect.top;
  const isAfterAddToCartBtn =
    !staticAddCartBtnInView && typeof staticAddCartBtnRectTop === 'number' && staticAddCartBtnRectTop < 0;
  const productParentSkuPrice = product.price_range?.minimum_price?.final_price;

  const chooseSizeProductImageUrl =
    Array.isArray(mediaGalleryEntries) && mediaGalleryEntries.length
      ? mediaGalleryEntries.reduce((acc, cur) => {
          if (cur && cur.url && !cur.disabled && (!acc || cur.position < acc.position)) {
            return cur;
          }

          return acc;
        }, null)?.url || null
      : null;

  return (
    <Fragment>
      <ProductBreadcrumb
        productDetails={productDetails}
        breadcrumbsData={breadcrumbsData}
      />
      {isAfterAddToCartBtn && product.__typename === 'ConfigurableProduct' ? (
        <div
          className={clsx(
            'fixed bottom-0 left-0 z-50 w-full',
            'bg-white shadow-[0px_-4px_15px_0_rgba(0,0,0,0.08)] lg_bg-[#052C45]',
            'px-3 py-5 lg_py-0 lg_h-[80px]',
            'animate-in fade-in-0',
          )}
        >
          <div className='mx-auto lg_flex lg_h-full lg_items-center lg_justify-between lg_px-[240px]'>
            <div className='hidden lg_flex lg_items-center lg_justify-center lg_gap-x-5'>
              <Image
                src={chooseSizeProductImageUrl || product.small_image}
                width={1265}
                height={1600}
                alt={transformedProductName}
                className='h-[60px] w-12 object-cover'
              />
              <div>
                <div className='font-teko-medium text-[22px] font-medium uppercase lg_text-white'>{transformedProductName}</div>
                {productParentSkuPrice && productParentSkuPrice.currency ? (
                  <div className='font-roboto-medium text-base font-medium lg_text-white'>
                    <Price
                      currencyCode={productParentSkuPrice.currency}
                      value={productParentSkuPrice.value}
                    />
                  </div>
                ) : null}
              </div>
            </div>
            <button
              type='button'
              className={clsx(
                'font-roboto-medium text-base font-medium text-white lg_text-lg',
                'flex h-10 w-full items-center justify-center lg_w-auto lg_px-[19px] lg_py-[14px]',
                'rounded-[102px] bg-black lg_bg-white lg_text-[#052C45] lg_hover_bg-gray-100',
              )}
              onClick={() => {
                if (selectedOptionsArray.length === (product.configurable_options?.length || 0)) {
                  form.handleSubmit(onSubmit)();
                } else {
                  productOptionsRef.current?.scrollIntoView({
                    block: 'center',
                    behavior: 'smooth',
                  });
                }
              }}
            >
              {selectedOptionsArray.length === (product.configurable_options?.length || 0) 
                ? (isOutOfStock ? 'Notify Me' : (isAdding ? 'Adding...' : 'Add to Cart')) 
                : 'Choose Your Size'}
            </button>
          </div>
        </div>
      ) : null}
      <div className='mx-auto max-w-site  '>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className='flex flex-col gap-x-10 lg_flex-row'>
              <div className='px-3 lg_hidden'>{productInfoHeader(true)}</div>
              <section className='mt-5 w-full lg_mt-0 lg_flex-shrink lg_basis-[900px]'>
                <ProductImageCarousel
                  images={mediaGalleryEntries}
                  name={transformedProductName}
                  sku={product.sku}
                  seoColorLabel={seoColorLabel}
                  videoUrl={product.video_url}
                />
              </section>

              <div className='basis-full lg_shrink-0 lg_basis-[500px]'>
                <section>
                  <div className='hidden border-b-1 border-solid border-e0e0e0 pb-[30px] lg_block'>
                    {productInfoHeader(false)}
                  </div>

                  <div className=' mt-5  px-3  lg_mt-[30px] lg_px-0 '>
                    <div className='border-b-1 border-solid border-e0e0e0 pb-5 lg_pb-[30px]'>
                      <div className='flex items-center justify-start gap-2'>
                        <p className='font-roboto-medium text-2xl font-medium leading-none'>
                          <Price
                            currencyCode={productDetails.price.currency}
                            value={finalPrice || productDetails.price.value}
                          />
                        </p>
                        <p className='font-roboto-regular text-base leading-none text-[#929292] line-through lg_leading-none'>
                          {hasSpecialPrice && regularPrice && (
                            <Price
                              currencyCode={productDetails.price.currency}
                              value={regularPrice}
                            />
                          )}
                        </p>
                        <EarnPoints price={finalPrice || productDetails.price.value} />
                      </div>

                      <SezzlePay
                        finalPrice={finalPrice}
                        productDetails={productDetails}
                      />
                    </div>
                  </div>
                </section>

                <ProductFormError
                  errorMessage={errorMessage}
                  failedToCart={failedToCart}
                />

                {hasConfigurableOptions || hasSizeChart ? (
                  <section className='mt-5 px-3 lg_mt-[30px] lg_px-0 '>
                    <div
                      className='border-b-1 border-solid border-e0e0e0  pb-5 lg_pb-[30px]'
                      ref={productOptionsRef}
                    >
                      <StandardOptions
                        configurableOptions={product.configurable_options}
                        variants={product.variants}
                        product={product}
                        optionSpecs={optionSpecs}
                        handleClick={handleClick}
                        handleColorLabelChange={handleColorLabelChange}
                      />
                      {hasSizeChart ? (
                        <FindYourSize
                          className={clsx('flex', hasConfigurableOptions && 'mt-5 lg_mt-[30px]')}
                          sizeChartInfo={sizeChartInfo}
                        />
                      ) : null}
                    </div>
                  </section>
                ) : null}

                <div className='mt-5 px-3 lg_mt-[30px] lg_px-0'>
                  <div className='border-b-1 border-solid border-e0e0e0 pb-5 lg_pb-[30px]'>
                    <AddCartButton
                      isPreOrder={isPreOrder}
                      note={note}
                      isOutOfStock={isOutOfStock}
                      isAdding={isAdding}
                      label={label}
                      product={product}
                      item={item}
                      ref={addCartBtnRef}
                    />
                  </div>
                </div>
                <ProductBenefitsAccordion isSignedIn={isSignedIn} />
                <div className='mt-5 flex flex-col gap-y-5 px-3 lg_mt-[30px] lg_gap-y-[30px] lg_px-0'>
                  <ProductDescription productDetails={productDetails} />
                  <ShowAttributes
                    attributes={product.show_attribute}
                    childAttributes={item?.product?.show_attribute}
                  />
                </div>
              </div>
            </div>
          </form>
        </Form>
      </div>

      <NotifyMeDialog
        open={notifyModalVisible}
        onSetOpen={setNotifyModalVisible}
        product_id={product.id}
        variants={product.variants}
        selectedOptionsArray={selectedOptionsArray}
        configurable_options={product.configurable_options}
        productDetailsName={transformedProductName}
        productSku={productDetails.sku}
      />
    </Fragment>
  );
};

export default ProductFullDetail;
